import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Fingerprint, KeyRound, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import {
  decodeCreationOptions, encodeAttestation, isWebAuthnSupported, LS_KEYS,
} from "@/lib/quickLogin";

export default function QuickLoginSettings() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/quick-login/devices");
      setDevices(data.devices || []);
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const enablePin = async () => {
    if (pin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    if (!/^\d+$/.test(pin)) { toast.error("PIN must be digits only"); return; }
    if (pin !== pinConfirm) { toast.error("PINs don't match"); return; }
    setBusy(true);
    try {
      const deviceName = `${navigator.platform || "Device"} · ${new Date().toLocaleDateString()}`;
      const { data } = await api.post("/quick-login/pin/enable", { pin, device_name: deviceName });
      localStorage.setItem(LS_KEYS.pin_device, data.device_id);
      toast.success("PIN login enabled on this device");
      setPinModalOpen(false); setPin(""); setPinConfirm("");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const enableFingerprint = async () => {
    if (!isWebAuthnSupported()) { toast.error("This browser doesn't support fingerprint / Face ID"); return; }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/quick-login/webauthn/register-options");
      const publicKey = decodeCreationOptions(opts);
      const cred = await navigator.credentials.create({ publicKey });
      const encoded = encodeAttestation(cred);
      const deviceName = `${navigator.platform || "Device"} biometric`;
      const { data } = await api.post("/quick-login/webauthn/register-verify", {
        device_id: crypto.randomUUID(),
        device_name: deviceName,
        credential: encoded,
      });
      localStorage.setItem(LS_KEYS.webauthn_device, data.device_id);
      toast.success("Fingerprint / Face ID login enabled");
      await load();
    } catch (err) {
      if (err?.name === "NotAllowedError") toast.error("Setup cancelled");
      else toast.error(formatApiError(err.response?.data?.detail) || err.message || "WebAuthn error");
    } finally { setBusy(false); }
  };

  const remove = async (device_id, type) => {
    if (!window.confirm("Remove this quick-login device? You'll need to sign in with email + password.")) return;
    try {
      await api.delete(`/quick-login/devices/${device_id}`);
      // Clean local storage if this was the current device
      if (localStorage.getItem(LS_KEYS.pin_device) === device_id) localStorage.removeItem(LS_KEYS.pin_device);
      if (localStorage.getItem(LS_KEYS.webauthn_device) === device_id) localStorage.removeItem(LS_KEYS.webauthn_device);
      toast.success("Device removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const localPinId = localStorage.getItem(LS_KEYS.pin_device);
  const localWaId = localStorage.getItem(LS_KEYS.webauthn_device);
  const hasPinOnThisDevice = devices.some(d => d.type === "pin" && d.device_id === localPinId);
  const hasWaOnThisDevice = devices.some(d => d.type === "webauthn" && d.device_id === localWaId);

  return (
    <div className="nb-card p-6 mt-6" data-testid="quick-login-settings">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={18} className="text-nb-cyan"/>
        <h2 className="font-heading font-semibold text-white">Quick Login</h2>
      </div>
      <p className="text-xs text-slate-400 mb-5">Unlock the app instantly with your fingerprint / Face ID or a 4-digit PIN on this device. Your password is still your master key — enable these for speed, not security downgrade.</p>

      <div className="grid md:grid-cols-2 gap-3 mb-5">
        <button
          data-testid="quick-login-enable-fingerprint"
          onClick={enableFingerprint}
          disabled={busy || hasWaOnThisDevice || !isWebAuthnSupported()}
          className={`p-4 rounded-xl border ${hasWaOnThisDevice ? "border-[#8AE234]/40 bg-[#8AE234]/5" : "border-white/10 hover:border-nb-cyan/40"} transition text-left group disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${hasWaOnThisDevice ? "bg-[#8AE234]/20 text-[#8AE234]" : "bg-nb-cyan/10 text-nb-cyan"}`}>
              <Fingerprint size={20}/>
            </div>
            <div>
              <div className="text-white font-semibold text-sm">Fingerprint / Face ID</div>
              <div className="text-[11px] text-slate-400">{hasWaOnThisDevice ? "Enabled on this device" : isWebAuthnSupported() ? "Tap to enable" : "Not supported here"}</div>
            </div>
          </div>
        </button>

        <button
          data-testid="quick-login-enable-pin"
          onClick={()=>setPinModalOpen(true)}
          disabled={busy || hasPinOnThisDevice}
          className={`p-4 rounded-xl border ${hasPinOnThisDevice ? "border-[#8AE234]/40 bg-[#8AE234]/5" : "border-white/10 hover:border-nb-cyan/40"} transition text-left group disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${hasPinOnThisDevice ? "bg-[#8AE234]/20 text-[#8AE234]" : "bg-nb-cyan/10 text-nb-cyan"}`}>
              <KeyRound size={20}/>
            </div>
            <div>
              <div className="text-white font-semibold text-sm">4-digit PIN</div>
              <div className="text-[11px] text-slate-400">{hasPinOnThisDevice ? "Enabled on this device" : "Tap to set a PIN"}</div>
            </div>
          </div>
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading devices…</div>
      ) : devices.length === 0 ? (
        <div className="text-xs text-slate-500">No quick-login devices yet.</div>
      ) : (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Active devices ({devices.length})</div>
          {devices.map((d) => (
            <div key={d.device_id} data-testid={`quick-login-device-${d.device_id}`} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/20 px-3 py-2 text-sm">
              {d.type === "pin" ? <KeyRound size={14} className="text-nb-cyan"/> : <Fingerprint size={14} className="text-nb-cyan"/>}
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs truncate">{d.device_name || d.type}</div>
                <div className="text-[10px] text-slate-500">Added {new Date(d.created_at).toLocaleDateString()} {d.last_used_at ? `· last used ${new Date(d.last_used_at).toLocaleDateString()}` : ""}</div>
              </div>
              <button onClick={()=>remove(d.device_id, d.type)} className="text-red-400/70 hover:text-red-300 p-1" data-testid={`quick-login-remove-${d.device_id}`}><Trash2 size={12}/></button>
            </div>
          ))}
        </div>
      )}

      {pinModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur" onClick={()=>setPinModalOpen(false)}>
          <div className="w-full max-w-sm nb-card p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1"><KeyRound size={16} className="text-nb-cyan"/><h3 className="font-heading text-white">Set a 4-digit PIN</h3></div>
            <p className="text-xs text-slate-400 mb-4">This PIN unlocks the app on this device only. It's not your password.</p>
            <div className="space-y-3">
              <input data-testid="pin-input" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} autoFocus
                value={pin} onChange={(e)=>setPin(e.target.value.replace(/\D/g,""))}
                className="nb-input text-center text-2xl tracking-[0.5em] font-mono" placeholder="••••"/>
              <input data-testid="pin-confirm" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8}
                value={pinConfirm} onChange={(e)=>setPinConfirm(e.target.value.replace(/\D/g,""))}
                className="nb-input text-center text-2xl tracking-[0.5em] font-mono" placeholder="Confirm"/>
              <div className="flex gap-2">
                <button onClick={()=>{ setPinModalOpen(false); setPin(""); setPinConfirm(""); }} className="nb-btn-secondary text-sm">Cancel</button>
                <button data-testid="pin-save" onClick={enablePin} disabled={busy || pin.length < 4} className="nb-btn-primary text-sm flex-1">{busy?"Saving…":"Enable PIN"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
